import { toast as sonnerToast } from 'sonner';

// Enhanced toast utilities with better theming support
export const toast = {
  success: (message: string, options?: { duration?: number; description?: string; action?: any }) => {
    return sonnerToast.success(message, {
      duration: options?.duration || 4000,
      description: options?.description,
      action: options?.action,
    });
  },

  error: (message: string, options?: { duration?: number; description?: string; action?: any }) => {
    return sonnerToast.error(message, {
      duration: options?.duration || 5000, // Longer for errors
      description: options?.description,
      action: options?.action,
    });
  },

  warning: (message: string, options?: { duration?: number; description?: string; action?: any }) => {
    return sonnerToast.warning(message, {
      duration: options?.duration || 4000,
      description: options?.description,
      action: options?.action,
    });
  },

  info: (message: string, options?: { duration?: number; description?: string; action?: any }) => {
    return sonnerToast.info(message, {
      duration: options?.duration || 4000,
      description: options?.description,
      action: options?.action,
    });
  },

  loading: (message: string, options?: { duration?: number; description?: string }) => {
    return sonnerToast.loading(message, {
      duration: options?.duration || 0, // Loading toasts don't auto-dismiss
      description: options?.description,
    });
  },

  promise: <T>(
    promise: Promise<T>,
    {
      loading,
      success,
      error,
    }: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    }
  ) => {
    return sonnerToast.promise(promise, {
      loading,
      success,
      error,
    });
  },

  dismiss: (toastId?: string | number) => {
    return sonnerToast.dismiss(toastId);
  },

  // Custom themed toasts for specific use cases
  ga4: {
    success: (message: string) => toast.success(message, {
      description: '🔧 GA4 metadata processing in background'
    }),
    
    connected: (propertyName: string) => toast.success(
      `🎉 Successfully connected ${propertyName}!`,
      { 
        description: 'Your GA4 data is now ready for analysis' 
      }
    ),
    
    reconnected: () => toast.success(
      'Successfully reconnected GA4!',
      { 
        description: 'Tokens updated and connection restored' 
      }
    ),
    
    error: (message: string) => toast.error(
      `GA4 Connection Error`,
      { 
        description: message,
        action: {
          label: 'Retry',
          onClick: () => window.location.reload()
        }
      }
    ),
  },

  onboarding: {
    welcome: (hasGa4: boolean) => toast.success(
      '🎉 Welcome to Crutan!',
      {
        description: hasGa4 
          ? 'Your GA4 setup is complete!' 
          : 'You can connect GA4 later from project settings.',
        duration: 6000
      }
    ),
    
    projectCreated: () => toast.success(
      'Project setup completed!',
      { description: 'Moving to the next step...' }
    ),
    
    step: (message: string) => toast.info(message, {
      duration: 3000
    }),
  },

  auth: {
    welcome: () => toast.success('Welcome back!'),
    
    accountCreated: () => toast.success(
      'Account created successfully!',
      { description: 'Please check your email to verify your account.' }
    ),
    
    loggedOut: () => toast.success('Logged out successfully'),
    
    profileUpdated: () => toast.success('Profile updated successfully'),
    
    passwordChanged: () => toast.success('Password changed successfully'),
    
    loginError: (message?: string) => toast.error(
      message || 'Login failed',
      { description: 'Please check your credentials and try again.' }
    ),
    
    registrationError: (message?: string) => toast.error(
      message || 'Registration failed',
      { description: 'Please check your information and try again.' }
    ),
  },

  project: {
    created: () => toast.success('Project created successfully'),
    
    updated: () => toast.success('Project updated successfully'),
    
    deleted: () => toast.success('Project deleted successfully'),
    
    error: (action: string, message?: string) => toast.error(
      `Failed to ${action} project`,
      { description: message || 'Please try again.' }
    ),
  },

  connection: {
    synchronized: () => toast.success('Data synchronized successfully'),
    
    refreshed: () => toast.success('Connections refreshed'),
    
    switched: (propertyName: string) => toast.success(
      `Successfully switched to ${propertyName}!`,
      { description: '🔧 Setting up metadata for the new property...' }
    ),
    
    error: (action: string, message?: string) => toast.error(
      `Failed to ${action}`,
      { description: message || 'Please try again.' }
    ),
  },
};

export default toast; 